import React, { useEffect, useState } from 'react';
import { Button } from '@chakra-ui/react';
import axios from 'axios';
import AutoComplete from 'react-select';
import InputField from 'components/fields/InputField';
import { AgentData, FeeGroupAssignment } from '../types';
import AgentFeeGroupAssignment from '../components/AgentFeeGroupAssignment';

interface AgentsStepProps {
  agents: AgentData[];
  onAdd: () => void;
  onRemove: (tempId: string) => void;
  onUpdate: (tempId: string, data: Partial<AgentData>) => void;
  onAddFeeGroupAssignment: (agentTempId: string) => void;
  onRemoveFeeGroupAssignment: (agentTempId: string, assignmentTempId: string) => void;
  onUpdateFeeGroupAssignment: (agentTempId: string, assignmentTempId: string, data: Partial<FeeGroupAssignment>) => void;
  isProcessing?: boolean;
}

interface DialcodeOption {
  value: string;
  label: string;
}

const AgentsStep: React.FC<AgentsStepProps> = ({
  agents,
  onAdd,
  onRemove,
  onUpdate,
  onAddFeeGroupAssignment,
  onRemoveFeeGroupAssignment,
  onUpdateFeeGroupAssignment,
  isProcessing = false,
}) => {
  const [dialcodeOptions, setDialcodeOptions] = useState<DialcodeOption[]>([]);
  const [isDialcodeLoading, setIsDialcodeLoading] = useState(true);
  const [emailErrors, setEmailErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      setIsDialcodeLoading(true);
      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/api/datasource/countries`
      );

      const countries = response?.data || [];
      const dialcodeMap: Record<string, string> = {};

      countries.forEach((c: any) => {
        if (c.dialcode !== null) {
          if (c.dialcode in dialcodeMap) {
            dialcodeMap[c.dialcode] = dialcodeMap[c.dialcode] + ', ' + c.country_name;
          } else {
            dialcodeMap[c.dialcode] = '(+' + c.dialcode + ') ' + c.country_name;
          }
        }
      });

      const options = Object.keys(dialcodeMap)
        .sort((a, b) => parseInt(a) - parseInt(b))
        .map(key => ({
          value: key,
          label: dialcodeMap[key],
        }));

      setDialcodeOptions(options);
    } catch (error) {
      console.error('Error fetching countries', error);
    } finally {
      setIsDialcodeLoading(false);
    }
  };

  const validateEmail = (email: string): boolean => {
    if (email === '') return true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (tempId: string, value: string) => {
    onUpdate(tempId, { email: value });
    if (!validateEmail(value)) {
      setEmailErrors(prev => ({
        ...prev,
        [tempId]: 'Invalid email format',
      }));
    } else {
      setEmailErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[tempId];
        return newErrors;
      });
    }
  };

  const handleMobileChange = (tempId: string, value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    onUpdate(tempId, { mobile: numericValue });
  };

  const getAgentDisplayName = (agent: AgentData, index: number): string => {
    if (agent.firstname || agent.lastname) {
      return `${agent.firstname} ${agent.lastname}`.trim();
    }
    return `Agent ${index + 1}`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Agents</h3>
          <p className="mt-1 text-sm text-gray-500">
            Configure agents with their fee group assignments.
          </p>
        </div>
        <Button
          onClick={onAdd}
          disabled={isProcessing}
          className="btn btn-default flex items-center gap-2"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Agent
        </Button>
      </div>

      {agents.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-300 p-12 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <h3 className="mt-4 text-sm font-medium text-gray-900">No agents</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by adding a new agent.
          </p>
          <Button
            onClick={onAdd}
            disabled={isProcessing}
            className="btn btn-primary mt-4"
          >
            Add Agent
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {agents.map((agent, index) => (
            <div
              key={agent.tempId}
              className="relative rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
            >
              {/* Header */}
              <div className="mb-6 flex items-center justify-between border-b border-gray-100 pb-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-600">
                    {index + 1}
                  </span>
                  <h4 className="text-base font-semibold text-gray-900">
                    {getAgentDisplayName(agent, index)}
                  </h4>
                </div>
                {agents.length > 1 && (
                  <button
                    type="button"
                    onClick={() => onRemove(agent.tempId)}
                    disabled={isProcessing}
                    className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  </button>
                )}
              </div>

              {/* Agent Personal Information */}
              <div className="mb-6 space-y-4 border-b border-gray-200 pb-6">
                <p className="text-md font-medium tracking-normal text-gray-700">
                  Personal Information
                </p>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <InputField
                    disabled={isProcessing}
                    variant="auth"
                    extra="shadow-sm"
                    label="First Name"
                    required={true}
                    placeholder="Enter first name"
                    id={`firstname_${agent.tempId}`}
                    value={agent.firstname}
                    type="text"
                    autoComplete="off"
                    onChange={e => onUpdate(agent.tempId, { firstname: e.target.value })}
                  />

                  <InputField
                    disabled={isProcessing}
                    variant="auth"
                    extra="shadow-sm"
                    label="Last Name"
                    required={true}
                    placeholder="Enter last name"
                    id={`lastname_${agent.tempId}`}
                    value={agent.lastname}
                    type="text"
                    autoComplete="off"
                    onChange={e => onUpdate(agent.tempId, { lastname: e.target.value })}
                  />

                  <InputField
                    disabled={isProcessing}
                    variant="auth"
                    extra="shadow-sm"
                    label="Business Name"
                    required={true}
                    placeholder="Enter business name"
                    id={`business_name_${agent.tempId}`}
                    value={agent.businessName}
                    type="text"
                    autoComplete="off"
                    onChange={e => onUpdate(agent.tempId, { businessName: e.target.value })}
                  />

                  <InputField
                    disabled={isProcessing}
                    variant="auth"
                    extra="shadow-sm"
                    label="Email"
                    required={true}
                    placeholder="Enter email"
                    id={`email_${agent.tempId}`}
                    value={agent.email}
                    type="email"
                    autoComplete="off"
                    onChange={e => handleEmailChange(agent.tempId, e.target.value)}
                    error={emailErrors[agent.tempId]}
                  />

                  <div>
                    <label className="text-sm font-medium text-gray-800">
                      Dialcode <span className="text-red-500">*</span>
                    </label>
                    <AutoComplete
                      isDisabled={isDialcodeLoading || isProcessing}
                      placeholder={isDialcodeLoading ? 'Loading...' : 'Please Select'}
                      options={dialcodeOptions}
                      value={dialcodeOptions.find(opt => opt.value === agent.mobileDialcode) || null}
                      classNamePrefix="custom-autocomplete"
                      className="autocomplete-override"
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                      onChange={(item: any) =>
                        onUpdate(agent.tempId, { mobileDialcode: item?.value || '' })
                      }
                      isClearable
                    />
                  </div>

                  <InputField
                    disabled={isProcessing}
                    variant="auth"
                    extra="shadow-sm"
                    label="Phone"
                    required={true}
                    placeholder="Enter mobile number"
                    id={`mobile_${agent.tempId}`}
                    value={agent.mobile}
                    type="text"
                    autoComplete="off"
                    onChange={e => handleMobileChange(agent.tempId, e.target.value)}
                  />
                </div>
              </div>

              {/* Fee Group Assignments */}
              <AgentFeeGroupAssignment
                assignments={agent.feeGroupAssignments}
                onAdd={() => onAddFeeGroupAssignment(agent.tempId)}
                onRemove={assignmentTempId => onRemoveFeeGroupAssignment(agent.tempId, assignmentTempId)}
                onUpdate={(assignmentTempId, data) => onUpdateFeeGroupAssignment(agent.tempId, assignmentTempId, data)}
                disabled={isProcessing}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AgentsStep;
